/* global runtime */
// Runtime, brukerpreferanser, Firebase-init og autoriserte Functions-kall.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
let runtime = {
  configured: false,
  firebaseApp: null,
  auth: null,
  db: null,
  storage: null,
  user: null,
  // Status for the verification message requested during this browser session.
  // Firebase delivery is asynchronous, but an immediate send error must be visible
  // to the account-verification screen instead of disappearing in the console.
  emailVerificationDelivery: null,
  userProfile: null,
  memberships: [],
  businessMembers: [],
  outgoingInvites: [],
  incomingInvites: [],
  billing: null,
  activeBusinessId: null, rowDomains: [], // raddomener (stateRows.js) med markør på app/state-roten for det aktive foretaket
  firmMemberships: [],
  // Byraaene som har et aktivt oppdrag paa foretaket brukeren staar i. Klientens
  // side av oppdraget — leses av eier/admin, og er porten til chatten: ingen
  // aktivt oppdrag, ingen dialog aa aapne.
  firmEngagements: [],
  demoAccount: false,
  appState: null,
  // `updatedAt` fra businesses/{id}/app/state. Brukes av avgrensede
  // read-model-cacher til aa vite om en Function-respons og Firestore-lytteren
  // beskriver samme state, uten aa sammenligne hele foretaksobjektet.
  appStateRevision: '',
  persistedAppStateRevision: '',
  // `updatedAt` per aktivt state-shard (stateShards.js), samme rolle for shard-dokumentene.
  persistedShardRevisions: {},
  // null = ikke forsokt lest enna. false = lesingen feilet, og da skal
  // personaldokumentet ikke skrives (se persistCurrentState).
  personnelStateReadable: null,
  loading: true,
  error: null,
  listeners: new Set(),
  unsubscribeState: null,
  bootStarted: false,
  saveTimer: null,
  // Businesses whose app document is identified as demo seed data for another
  // user. Both UI state changes and persistence are blocked for those IDs.
  stateWriteBlockedBusinessIds: new Set(),
  activeYearSelectionsByBusiness: new Map(),
};
let lastEmittedAppState = runtime.appState;
let lastEmittedAppStateRevision = runtime.appStateRevision;
let localAppStateRevisionSequence = 0;
const mutationResponseOrder = window.PlummeMutationResponseOrder.createMutationResponseOrder();
const emitRuntime = () => {
  // En lokal state-endring har en ny objektidentitet, men ingen Firestore-
  // revision ennaa. Gi den en unik midlertidig revision slik at avledede
  // backend-cacher ikke forveksler den med staten de nettopp ble bygget fra.
  if (runtime.appState !== lastEmittedAppState && runtime.appStateRevision === lastEmittedAppStateRevision) {
    localAppStateRevisionSequence += 1;
    runtime.appStateRevision = `local:${Date.now()}:${localAppStateRevisionSequence}`;
  }
  lastEmittedAppState = runtime.appState;
  lastEmittedAppStateRevision = runtime.appStateRevision;
  publishGlobals(runtime.appState);
  runtime.listeners.forEach((listener) => listener({ ...runtime }));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
};
const sendEmailVerificationWithStatus = async (user) => {
  runtime.emailVerificationDelivery = 'sending';
  emitRuntime();
  try {
    await user.sendEmailVerification();
    runtime.emailVerificationDelivery = 'sent';
    emitRuntime();
    return true;
  } catch (error) {
    runtime.emailVerificationDelivery = 'failed';
    emitRuntime();
    throw error;
  }
};

// App-only mutation responses must not replace the separately loaded personnel data,
// including legacy personnel fields that may still exist in app/state.
const mergeAppMutationState = (state) => {
  state = window.PlummeStateRows.mergeMutation(state, runtime.appState, runtime.activeBusinessId);
  const responseOrder = state && mutationResponseOrder.accept(state, runtime.activeBusinessId);
  if (!responseOrder?.accepted) return runtime.appState;
  const merged = mergeState(PlummePersonnelState.mergePersonnelState(
    PlummePersonnelState.stripPersonnelState(state), runtime.appState));
  return window.PlummeAccountingJournalPagination.mergeMutationReadProof(runtime, merged, state);
};
const activeBusinessKey = (userId) => `plumme-active-business-${userId}`;
const normalizeBooleanMap = (value = {}) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key, entry]) => typeof key === 'string' && typeof entry === 'boolean')
      .map(([key, entry]) => [key, entry]),
  );
};
const normalizeAccountCodeList = (value) => (Array.isArray(value)
  ? [...new Set(value.map((code) => String(code || '').trim()).filter(Boolean))]
  : undefined);
const uiLocale = () => window.PlummeI18n?.getLocale?.() || 'no';
const normalizeAccountFavoritesByBusiness = (value = {}) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return Object.fromEntries(Object.entries(value)
    .map(([businessId, codes]) => [String(businessId || '').trim(), normalizeAccountCodeList(codes)])
    .filter(([businessId, codes]) => businessId && Array.isArray(codes)));
};
const normalizeUserPreferences = (preferences = {}) => {
  const source = preferences && typeof preferences === 'object' && !Array.isArray(preferences) ? preferences : {};
  const notifications = source.notifications && typeof source.notifications === 'object' ? source.notifications : {}, hasHiddenPages = source.hiddenPages && typeof source.hiddenPages === 'object' && !Array.isArray(source.hiddenPages);
  return stripUndefinedDeep({
    locale: typeof source.locale === 'string' && source.locale ? source.locale : undefined,
    theme: typeof source.theme === 'string' && source.theme ? source.theme : undefined,
    mascots: typeof source.mascots === 'boolean' ? source.mascots : undefined,
    salesMode: typeof source.salesMode === 'string' && source.salesMode ? source.salesMode : undefined,
    purchaseMode: typeof source.purchaseMode === 'string' && source.purchaseMode ? source.purchaseMode : undefined,
    bankMode: typeof source.bankMode === 'string' && source.bankMode ? source.bankMode : undefined,
    demoResetShortcutBusinessIds: normalizeBooleanMap(source.demoResetShortcutBusinessIds),
    hiddenPages: hasHiddenPages ? normalizeBooleanMap(source.hiddenPages) : undefined,
    // Saldo-kortet pa Hjem: hvilke hovedbokskontoer som er huket av, hvilke brukeren
    // har lagt til fra kontoplanen, og hvilke som er fjernet fra kortet. Tom liste er
    // et gyldig valg (bruker har skrudd av alle), sa den lagres i stedet for a falle
    // tilbake pa standardutvalget ved neste innlasting.
    homeBalanceAccounts: normalizeAccountCodeList(source.homeBalanceAccounts),
    homeBalanceExtraAccounts: normalizeAccountCodeList(source.homeBalanceExtraAccounts),
    homeBalanceHiddenAccounts: normalizeAccountCodeList(source.homeBalanceHiddenAccounts),
    accountFavoritesByBusiness: normalizeAccountFavoritesByBusiness(source.accountFavoritesByBusiness),
    notifications: {
      deadlines: typeof notifications.deadlines === 'boolean' ? notifications.deadlines : undefined,
      overdueInvoices: typeof notifications.overdueInvoices === 'boolean' ? notifications.overdueInvoices : undefined,
      inbox: typeof notifications.inbox === 'boolean' ? notifications.inbox : undefined,
      tips: typeof notifications.tips === 'boolean' ? notifications.tips : undefined,
    },
  });
};
const applyUserPreferences = (preferences = {}) => {
  const normalized = normalizeUserPreferences(preferences);
  try {
    if (Object.prototype.hasOwnProperty.call(normalized, 'hiddenPages') && window.setHiddenPages) window.setHiddenPages(normalized.hiddenPages);
    if (normalized.theme) {
      const isDark = normalized.theme === 'dark';
      if (isDark) document.documentElement.dataset.dark = '1';
      else delete document.documentElement.dataset.dark;
      localStorage.setItem('plumme-dark', isDark ? '1' : '0');
      // la bryteren i topplinja og Innstillinger følge det backend nettopp satte
      window.dispatchEvent(new CustomEvent('plumme-dark-changed', { detail: isDark }));
    }
    // UI-språk. Uavhengig av foretakets land (`settings.country`), som eier
    // valuta, kontoplan og innleveringer. setLocale varsler React-treet selv.
    if (normalized.locale && window.PlummeI18n) window.PlummeI18n.setLocale(normalized.locale);
  } catch (error) {}
  return normalized;
};
// Kjorer vi paa utviklerens egen maskin?
//
// Dette er det ene spoersmaalet som avgjor hvor BEGGE halvdelene av backend
// ligger. Tidligere avgjorde det bare hvor HTTP-funksjonene laa, og resten —
// Firestore, Auth, Storage — gikk mot produksjon. Det var ikke et halvveis
// oppsett; det var to oppsett samtidig, og de kunne ikke snakke sammen.
//
// Konsekvensen var stille: en Firestore-utloest funksjon som `autopilotJobWorker`
// lytter paa den emulerte databasen. Klienten skrev jobbposten til produksjon.
// Der laa den, uten at noe plukket den opp. Fakturaen ble aldri sendt, og
// «Lagt i ko» var det siste som skjedde. Ingen feil noe sted — bare to systemer
// som pekte hver sin vei.
//
// I tillegg kjorte lokal, ugjennomgaatt funksjonskode mot ekte kundedata.
const PLUMME_EMULATOR_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0'];
const runningOnLocalhost = () => PLUMME_EMULATOR_HOSTS.includes(String(window.location?.hostname || '').trim().toLowerCase());
// Portene folger `emulators` i firebase.json. Endres de der, maa de endres her.
const PLUMME_EMULATOR_PORTS = { auth: 9099, firestore: 8081, storage: 9199, functions: 5305 };
const firebaseEmulatorPorts = () => ({ ...PLUMME_EMULATOR_PORTS, ...PLUMME_FIREBASE_CONFIG.emulatorPorts });
const isolatedEmulatorsOnly = () => PLUMME_FIREBASE_CONFIG.emulatorsOnly === true;
// En eksplisitt origin (satt ved bygg) betyr at noen har pekt appen et bestemt
// sted. Da skal ikke localhost-regelen overstyre det.
const functionsOriginConfigured = () => {
  if (isolatedEmulatorsOnly()) return '';
  const raw = String(window.PLUMME_FIREBASE_FUNCTIONS_ORIGIN || PLUMME_FIREBASE_CONFIG.functionsOrigin || '').trim();
  return raw && !raw.includes('%VITE_') && !raw.includes('__PLUMME_') ? raw.replace(/\/+$/, '') : '';
};
// Backend-valget er eksplisitt paa localhost:
// - standard: vanlig backend, selv om emulatorene kjorer ved siden av
// - `?backend=emulators`: full lokal emulator, inkludert Auth/Firestore
// - `?backend=production`: hele appen mot produksjon
//
// Valget ligger i nettleseren, ikke i en sporet fil, saa det kan ikke bli med i en
// commit ved et uhell. Og det er eksplisitt — det er hele forskjellen fra det
// stille tilbakefallet dette oppsettet finnes for aa hindre.
const PLUMME_DEV_BACKEND_KEY = 'plumme.devBackend';
const DEV_BACKEND_CHOICES = ['production', 'emulators'];
const devBackendFromSearch = () => {
  try {
    const fromSearch = String(new URLSearchParams(String(window.location?.search || '')).get('backend') || '').trim().toLowerCase();
    if (fromSearch) return fromSearch;
    const hashQuery = String(window.location?.hash || '').split('?')[1] || '';
    return String(new URLSearchParams(hashQuery).get('backend') || '').trim().toLowerCase();
  } catch (error) {
    return '';
  }
};
// Adressen bestemmer, og setter valget for de neste lastingene: en dev-app du maa
// huske aa legge til en parameter paa hver gang er en dev-app du glemmer.
const devBackendChoice = () => {
  if (isolatedEmulatorsOnly()) return 'emulators';
  const requested = devBackendFromSearch();
  try {
    if (requested === 'production' || requested === 'emulators') window.localStorage?.setItem?.(PLUMME_DEV_BACKEND_KEY, requested);
    else if (requested === 'default') window.localStorage?.removeItem?.(PLUMME_DEV_BACKEND_KEY);
    if (DEV_BACKEND_CHOICES.includes(requested)) return requested;
    return String(window.localStorage?.getItem?.(PLUMME_DEV_BACKEND_KEY) || '').trim().toLowerCase();
  } catch (error) {
    return requested;
  }
};
const usingFirebaseEmulators = () => !functionsOriginConfigured() && runningOnLocalhost() && devBackendChoice() === 'emulators';
const configuredFunctionsOrigin = () => {
  const configured = functionsOriginConfigured();
  if (configured) return configured;
  if (!usingFirebaseEmulators()) return '';
  const projectId = String(PLUMME_FIREBASE_CONFIG.projectId || '').trim();
  const region = String(PLUMME_FIREBASE_CONFIG.functionsRegion || 'europe-west1').trim() || 'europe-west1';
  return projectId ? `http://127.0.0.1:${firebaseEmulatorPorts().functions}/${projectId}/${region}` : '';
};
const functionUrl = (name) => {
  const origin = configuredFunctionsOrigin();
  return origin
    ? `${origin}/${name}`
    : `https://${PLUMME_FIREBASE_CONFIG.functionsRegion || 'europe-west1'}-${PLUMME_FIREBASE_CONFIG.projectId}.cloudfunctions.net/${name}`;
};

// Probe Auth and Firestore before SDK initialization: a Functions-only emulator
// leaves Firestore retrying against a silent port. Fail quickly and never fall
// back to production when the explicitly selected local services are missing.
const EMULATOR_PROBE_TIMEOUT_MS = 2500;
const emulatorPortAnswers = async (port) => {
  try {
    await withClientTimeout(
      fetch(`http://127.0.0.1:${port}/`, { mode: 'no-cors', cache: 'no-store' }),
      EMULATOR_PROBE_TIMEOUT_MS,
      'emulator-probe-timeout',
    );
    return true;
  } catch (error) {
    return false;
  }
};
// Auth og Firestore er de to appen ikke kommer opp uten. Storage brukes forst
// naar noe lastes opp, og skal ikke stoppe innlastingen.
const unreachableEmulators = async () => {
  const required = [['auth', firebaseEmulatorPorts().auth], ['firestore', firebaseEmulatorPorts().firestore]];
  const answers = await Promise.all(required.map(([, port]) => emulatorPortAnswers(port)));
  return required.filter((_, index) => !answers[index]).map(([name, port]) => `${name} (${port})`);
};

// Kobler klienten til de samme emulatorene funksjonene kjorer i.
//
// Maa skje for forste lesing eller skriving — compat-SDK-en laaser vertsnavnet
// naar tilkoblingen aapnes, og et kall etterpaa kaster. Derfor rett etter at
// tjenestene er hentet, og bare én gang.
//
// Er emulatorene ikke startet, feiler kallene mot dem. Det er med vilje: en app
// som stille falt tilbake til produksjon ville skrevet ekte data fra en lokal
// gren, og det er den feilen dette oppsettet finnes for aa hindre.
let firebaseEmulatorsConnected = false;
const connectFirebaseEmulators = () => {
  if (firebaseEmulatorsConnected || !usingFirebaseEmulators()) return false;
  firebaseEmulatorsConnected = true;
  try {
    runtime.auth?.useEmulator?.(`http://127.0.0.1:${firebaseEmulatorPorts().auth}`, { disableWarnings: true });
    runtime.db?.useEmulator?.('127.0.0.1', firebaseEmulatorPorts().firestore);
    runtime.storage?.useEmulator?.('127.0.0.1', firebaseEmulatorPorts().storage);
    // Uten dette er det ikke tydelig hvilket datasett en tom flate kommer fra.
    console.info('[plumme] Firebase-emulatorene er i bruk (auth %d, firestore %d, storage %d, functions %d). Produksjonsdata er ikke i bruk.',
      firebaseEmulatorPorts().auth, firebaseEmulatorPorts().firestore, firebaseEmulatorPorts().storage, firebaseEmulatorPorts().functions);
    return true;
  } catch (error) {
    runtime.error = error?.message || 'Kunne ikke koble til Firebase-emulatorene.';
    return false;
  }
};
const initFirebase = async () => {
  if (isolatedEmulatorsOnly() && (!runningOnLocalhost() || !String(PLUMME_FIREBASE_CONFIG.projectId || '').startsWith('demo-') || Object.values(firebaseEmulatorPorts()).some(port => !Number.isInteger(port) || port < 1 || port > 65535))) {
    runtime.error = 'Isolated retest requires localhost, a demo project and valid emulator ports.';
    return false;
  }
  const required = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
  runtime.configured = Boolean(window.firebase) && required.every((key) => String(PLUMME_FIREBASE_CONFIG[key] || '').trim());
  if (!runtime.configured) return false;
  if (!runtime.firebaseApp) {
    // Kjorer en lokal app mot ekte kundedata, skal det staa i konsollen hver gang.
    if (runningOnLocalhost() && !usingFirebaseEmulators()) {
      console.warn('[plumme] Local app uses production Auth/Firestore. Functions origin:', configuredFunctionsOrigin() || 'production');
    }
    // Sjekken maa staa for tjenestene hentes: etter det er vertsnavnet laast, og
    // en Firestore-lesing mot en stille port henger i stedet for aa feile.
    if (usingFirebaseEmulators()) {
      const missing = await unreachableEmulators();
      if (missing.length) {
        runtime.error = `Firebase emulators are not responding: ${missing.join(', ')}. Start the full suite with “npx firebase emulators:start” — “--only functions” is not enough. The app does not connect to production automatically from localhost; to do that, open the app with “?backend=production”.`;
        console.error('[plumme] %s', runtime.error);
        return false;
      }
    }
    runtime.firebaseApp = firebase.apps && firebase.apps.length ? firebase.app() : firebase.initializeApp(PLUMME_FIREBASE_CONFIG);
    if (!usingFirebaseEmulators()) window.PlummeAppCheck?.activate?.(runtime.firebaseApp, { siteKey: window.PLUMME_APP_CHECK_SITE_KEY || PLUMME_FIREBASE_CONFIG.appCheckSiteKey, localhost: runningOnLocalhost() }); // before Firestore/Storage
    runtime.auth = firebase.auth();
    runtime.db = firebase.firestore();
    runtime.storage = firebase.storage ? firebase.storage() : null;
    connectFirebaseEmulators();
    void runtime.auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL).catch((error) => {
      runtime.error = error.message || 'Could not configure auth persistence.';
      emitRuntime();
    });
  }
  return true;
};

const withClientTimeout = (promise, timeoutMs, message, onTimeout) => {
  const duration = Number(timeoutMs || 0);
  if (!Number.isFinite(duration) || duration <= 0) return Promise.resolve(promise);
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      try { onTimeout?.(); } catch (_) {}
      reject(new Error(message));
    }, duration);
    Promise.resolve(promise).then(
      (value) => { window.clearTimeout(timer); resolve(value); },
      (error) => { window.clearTimeout(timer); reject(error); },
    );
  });
};

const authorizedPost = async (functionName, body = {}, { authTimeoutMs = 0, requestTimeoutMs = 0, timeoutMessage = '' } = {}) => {
  body = JSON.parse(JSON.stringify(body)); window.PlummeAccountingJournalRecovery.assertRequestAllowed(runtime, functionName, body);
  const responseOrderStamp = mutationResponseOrder.issue(runtime.activeBusinessId);
  await window.PlummeAssistantMutationApproval?.beforeHttp(functionName, body);
  const token = await withClientTimeout(
    runtime.auth.currentUser?.getIdToken(),
    authTimeoutMs,
    t('svc.authTimedOut'),
  );
  if (!token) throw new Error(t('svc.mustBeSignedIn'));
  window.PlummeAccountingJournalRecovery.assertRequestAllowed(runtime, functionName, body); const abortController = requestTimeoutMs > 0 && typeof AbortController !== 'undefined' ? new AbortController() : null;
  const response = await withClientTimeout(fetch(functionUrl(functionName), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(await window.PlummeAppCheck?.headers?.()) },
    body: JSON.stringify(body),
    ...(abortController ? { signal: abortController.signal } : {}),
  }), requestTimeoutMs, timeoutMessage || t('svc.requestTimedOut'), () => abortController?.abort());
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const invoiceMutationFunctions = new Set([
      'copyInvoiceHttp',
      'deleteInvoiceHttp',
      'upsertInvoiceHttp',
      'invoicePaymentMutationHttp',
      'invoicePublicLinkHttp',
      'bankReconciliationMutationHttp',
      'autopilotExecutionHttp',
      'yearClosingMutationHttp',
      'annualAccountsMutationHttp',
      'payrollRunMutationHttp',
      'vatMutationHttp',
      'businessProfileMutationHttp',
      'taxReturnMutationHttp',
      'manualPostingMutationHttp',
      'otherSaleMutationHttp',
      'purchaseMutationHttp',
      'downloadPayslipPdfHttp',
      'sendPayslipEmailHttp',
      'sendCustomerBillingGroupEmailHttp',
      'recurringAgreementMutationHttp',
      'sendCustomerConversationEmailHttp',
      'openAccountingImportDocumentHttp',
    ]);
    const missingInvoiceMutationFunction = response.status === 404 && invoiceMutationFunctions.has(functionName);
    const serverMessage = payload?.message || payload?.error || (missingInvoiceMutationFunction
      ? t('svc.backendActionMissing')
      : `HTTP ${response.status}`);
    // Serveren kjenner ikke brukerens språk. Sender den en domenekode, slås den
    // opp i katalogen; uten treff står serverens egen tekst igjen som fallback,
    // så kodene kan innføres én feil om gangen. Se docs/architecture/i18n.md.
    const error = new Error(
      PlummeI18n.responseMessage(payload, serverMessage),
    );
    error.status = response.status;
    error.code = payload?.error || '';
    error.domainCode = payload?.domainCode || '';
    // Strukturen bak meldingen, naar serveren sender en. Brukes av flater som
    // viser aarsakene som punkter i stedet for som en setning.
    error.details = payload?.details || null;
    throw error;
  }
  return mutationResponseOrder.stamp(payload || {}, responseOrderStamp);
};
